const API_BASE = import.meta.env.PROD ? 'https://api.perihel.io/api' : '/api';

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

export interface AccountBrief {
  whyNow: string;
  signalSummary?: string;
  nextAction: string;
  suggestedOpener: string;
  confidence?: 'high' | 'medium' | 'low' | string;
  generatedAt?: string;
  fingerprint?: string;
}

class ApiClient {
  private token: string | null = null;
  private tokenGetter: (() => Promise<string | null>) | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  /**
   * Wire a function (typically Clerk's getToken) that the client awaits on
   * every request. Without this, the cached token in localStorage goes stale
   * after Clerk's expiry (~60s) and every call 401s. With it, Clerk's
   * internal cache hands back a fresh JWT per request — fast, no manual
   * refresh.
   */
  setTokenGetter(getter: (() => Promise<string | null>) | null) {
    this.tokenGetter = getter;
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  private async resolveToken(): Promise<string | null> {
    if (this.tokenGetter) {
      try {
        const fresh = await this.tokenGetter();
        if (fresh) {
          this.token = fresh;
          return fresh;
        }
      } catch {
        // fall through to cached
      }
    }
    return this.getToken();
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { params, ...fetchOptions } = options;

    let url = `${API_BASE}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) {
          searchParams.set(key, String(value));
        }
      }
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(fetchOptions.headers as Record<string, string> || {}),
    };

    const token = await this.resolveToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: 'Request failed' })) as {
        error?: string; message?: string; detail?: string;
      };
      // Worker errors are shaped { error, detail }; surface both so the UI
      // doesn't just say "Summarization failed" with no clue what broke.
      const head = errorBody.error || errorBody.message || `HTTP ${response.status}`;
      throw new Error(errorBody.detail ? `${head}: ${errorBody.detail}` : head);
    }

    return response.json();
  }

  // Auth
  async login(email: string, password: string) {
    const result = await this.request<{
      token: string;
      user: { id: string; email: string; name: string; role: string };
      organization: { id: string; name: string; slug: string };
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(result.token);
    return result;
  }

  async register(email: string, password: string, name: string, orgName: string) {
    const result = await this.request<{
      token: string;
      user: { id: string; email: string; name: string; role: string };
      organization: { id: string; name: string; slug: string };
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, orgName }),
    });
    this.setToken(result.token);
    return result;
  }

  async getMe() {
    return this.request<{
      user: { id: string; email: string; name: string; role: string; settings: Record<string, unknown> };
      organization: { id: string; name: string; slug: string; settings: Record<string, unknown> };
    }>('/auth/me');
  }

  async syncUser(data: { name?: string; orgName?: string }) {
    return this.request<{
      user: { id: string; email: string; name: string; role: string; settings: Record<string, unknown> };
      organization: { id: string; name: string; slug: string; settings: Record<string, unknown> };
    }>('/auth/sync', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  logout() {
    this.setToken(null);
  }

  // Leads
  async getLeads(params?: {
    page?: number;
    pageSize?: number;
    filters?: string;
    sorts?: string;
    search?: string;
  }) {
    return this.request<{
      data: Lead[];
      total: number;
      page: number;
      pageSize: number;
      hasMore: boolean;
    }>('/leads', { params });
  }

  async getLead(id: string) {
    return this.request<{
      lead: Lead;
      engagements: Engagement[];
      conversations: Conversation[];
    }>(`/leads/${id}`);
  }

  async updateLead(id: string, data: Partial<Lead>) {
    return this.request<{ success: boolean }>(`/leads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async bulkUpdateLeads(ids: string[], updates: Partial<Lead>) {
    return this.request<{ success: boolean; count: number }>('/leads/bulk', {
      method: 'POST',
      body: JSON.stringify({ ids, updates }),
    });
  }

  async createLead(data: {
    name: string;
    profileUrl?: string;
    headline?: string;
    companyName?: string;
    location?: string;
    about?: string;
    profilePicture?: string;
  }) {
    return this.request<{ id: string }>('/leads', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        profileUrl: data.profileUrl || `manual://${Date.now()}`,
      }),
    });
  }

  // CRM entity deletes — distinct from deleteAccount() which deletes the
  // signed-in user's auth account.
  async deleteAccountRecord(id: string) {
    return this.request<{ ok: true }>(`/accounts/${id}`, { method: 'DELETE' });
  }
  async deleteContact(id: string) {
    return this.request<{ ok: true }>(`/contacts/${id}`, { method: 'DELETE' });
  }
  async deleteOpportunity(id: string) {
    return this.request<{ ok: true }>(`/opportunities/${id}`, { method: 'DELETE' });
  }

  async deleteLead(id: string) {
    return this.request<{ success: boolean }>(`/leads/${id}`, {
      method: 'DELETE',
    });
  }

  // Companies
  async getCompanies(params?: {
    page?: number;
    pageSize?: number;
    tier?: string;
    search?: string;
  }) {
    return this.request<{
      data: Company[];
      total: number;
      page: number;
      pageSize: number;
      hasMore: boolean;
    }>('/companies', { params });
  }

  async getCompany(id: string) {
    return this.request<{
      company: Company;
      leads: Lead[];
      conversations: Conversation[];
    }>(`/companies/${id}`);
  }

  async updateCompany(id: string, data: Partial<Company>) {
    return this.request<{ success: boolean }>(`/companies/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Conversations
  async getConversations(params?: {
    page?: number;
    pageSize?: number;
    leadId?: string;
    companyId?: string;
    type?: string;
  }) {
    return this.request<{
      data: Conversation[];
      total: number;
      page: number;
      pageSize: number;
      hasMore: boolean;
    }>('/conversations', { params });
  }

  async createConversation(data: Partial<Conversation>) {
    return this.request<{ id: string }>('/conversations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateConversation(id: string, data: Partial<Conversation>) {
    return this.request<Conversation>(`/conversations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async analyzeConversation(id: string) {
    return this.request<{ status: string }>(`/conversations/${id}/analyze`, {
      method: 'POST',
    });
  }

  // SOWs
  async getSOWs(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    leadId?: string;
    companyId?: string;
  }) {
    return this.request<{
      data: SOW[];
      total: number;
      page: number;
      pageSize: number;
      hasMore: boolean;
    }>('/sows', { params });
  }

  async getSOW(id: string) {
    return this.request<SOW>(`/sows/${id}`);
  }

  async generateSOW(data: {
    title?: string;
    leadId?: string;
    companyId?: string;
    serviceIds?: string[];
    context?: string;
  }) {
    return this.request<{ sowId: string; executionId: string }>('/sows/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async regenerateSOW(id: string, reason?: string, executionId?: string) {
    return this.request<{ executionId: string }>(`/sows/${id}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ reason, executionId }),
    });
  }

  async updateSOW(id: string, data: Partial<SOW>) {
    return this.request<{ success: boolean }>(`/sows/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async sendSOW(id: string) {
    return this.request<{ success: boolean }>(`/sows/${id}/send`, {
      method: 'POST',
    });
  }

  async getServices() {
    return this.request<ServiceCatalogItem[]>('/sows/services');
  }

  async createService(data: Omit<ServiceCatalogItem, 'id'>) {
    return this.request<{ id: string }>('/sows/services', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateService(id: string, data: Partial<ServiceCatalogItem>) {
    return this.request<{ success: boolean }>(`/sows/services/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteService(id: string) {
    return this.request<{ success: boolean }>(`/sows/services/${id}`, {
      method: 'DELETE',
    });
  }

  // Settings
  async updateUserSettings(settings: Record<string, unknown>) {
    return this.request<{ success: boolean }>('/auth/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }

  async updateOrgSettings(settings: Record<string, unknown>) {
    return this.request<{ success: boolean }>('/auth/org/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }

  // Activity Logs
  async getMyActivityLogs(params?: { limit?: number; securityOnly?: boolean }) {
    return this.request<{ logs: ActivityLog[] }>('/auth/activity/me', {
      params: {
        limit: params?.limit,
        securityOnly: params?.securityOnly,
      },
    });
  }

  async getActivityLogs(params?: { limit?: number; securityOnly?: boolean; userId?: string }) {
    return this.request<{ logs: ActivityLog[] }>('/auth/activity', {
      params: {
        limit: params?.limit,
        securityOnly: params?.securityOnly,
        userId: params?.userId,
      },
    });
  }

  // Delete user account and all associated data
  async deleteAccount() {
    return this.request<{ success: boolean }>('/auth/account', {
      method: 'DELETE',
    });
  }

  // Ouroboros
  async executeComponent(name: string, input: unknown, context?: { type: string; id: string }) {
    return this.request<{ executionId: string }>(`/ouroboros/execute/component/${name}`, {
      method: 'POST',
      body: JSON.stringify({ input, contextType: context?.type, contextId: context?.id }),
    });
  }

  async executePipeline(name: string, input: unknown, context?: { type: string; id: string }) {
    return this.request<{ executionId: string }>(`/ouroboros/execute/pipeline/${name}`, {
      method: 'POST',
      body: JSON.stringify({ input, contextType: context?.type, contextId: context?.id }),
    });
  }

  async getExecution(id: string) {
    return this.request<Execution>(`/ouroboros/executions/${id}`);
  }

  // Telemetry
  async trackEvents(events: TelemetryEvent[]) {
    return this.request<{ queued: number }>('/telemetry/events', {
      method: 'POST',
      body: JSON.stringify(events),
    });
  }

  async startSession(data: { userAgent: string; viewport: { width: number; height: number }; referrer?: string }) {
    return this.request<{ sessionId: string }>('/telemetry/session/start', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async endSession(sessionId: string) {
    return this.request<{ success: boolean }>(`/telemetry/session/${sessionId}/end`, {
      method: 'POST',
    });
  }

  async trackDwell(data: {
    sessionId: string;
    entityType: string;
    entityId: string;
    executionId?: string;
    durationMs: number;
    interactions: number;
    scrollDepth: number;
    startedAt: string;
    endedAt: string;
  }) {
    return this.request<{ success: boolean }>('/telemetry/dwell', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async submitFeedback(data: {
    executionId: string;
    signalType: string;
    positive: boolean;
    weight?: number;
    value?: string;
    reason?: string;
  }) {
    return this.request<{ id: string }>('/telemetry/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Analytics
  async getEventAnalytics(params?: { from?: string; to?: string; groupBy?: string }) {
    return this.request<Array<{ period: string; event_type: string; count: number }>>('/telemetry/analytics/events', { params });
  }

  async getComponentPerformance(params?: { from?: string; to?: string }) {
    return this.request<Array<{
      id: string;
      name: string;
      type: string;
      total_feedback: number;
      positive_count: number;
      avg_weight: number;
      avg_confidence: number;
    }>>('/telemetry/analytics/components', { params });
  }

  // Post Analytics (Extension)
  async getPostAnalytics() {
    return this.request<{
      summary: {
        totalPosts: number;
        totalLeads: number;
        totalQualified: number;
        totalDeals: number;
        totalRevenue: number;
        avgLeadsPerPost: number;
      };
      posts: TrackedPost[];
    }>('/extension/posts/analytics');
  }

  async getTrackedPosts() {
    return this.request<{
      data: TrackedPost[];
      total: number;
    }>('/extension/posts/analytics');
  }

  // Saved Views
  async getSavedViews(entityType: string) {
    return this.request<SavedView[]>(`/views?entityType=${entityType}`);
  }

  async createSavedView(data: Partial<SavedView>) {
    return this.request<{ id: string }>('/views', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ===========================================
  // CANONICAL ENTITIES (New Data Model)
  // ===========================================

  // Accounts (replaces Companies)
  async getAccounts(params?: {
    page?: number;
    pageSize?: number;
    state?: string;
    tier?: string;
    search?: string;
  }) {
    return this.request<{
      data: Account[];
      total: number;
      page: number;
      pageSize: number;
      hasMore: boolean;
    }>('/accounts', { params });
  }

  async getAccount(id: string) {
    return this.request<{
      account: Account;
      contacts: Contact[];
      opportunities: Opportunity[];
    }>(`/accounts/${id}`);
  }

  async createAccount(data: Partial<Account>) {
    return this.request<{ id: string }>('/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAccount(id: string, data: Partial<Account>) {
    return this.request<{ success: boolean }>(`/accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async qualifyAccount(id: string) {
    return this.request<{ success: boolean; icpScore: number; tier: string }>(`/accounts/${id}/qualify`, {
      method: 'POST',
    });
  }

  async addAccountSignal(id: string, signal: { type: string; strength: number; source: string }) {
    return this.request<{ success: boolean; signalScore: number }>(`/accounts/${id}/signals`, {
      method: 'POST',
      body: JSON.stringify(signal),
    });
  }

  // AI next-best-action brief for an account (why-now / next-action / opener).
  async getAccountBrief(id: string, opts?: { refresh?: boolean }) {
    return this.request<{ brief: AccountBrief; cached: boolean }>(`/accounts/${id}/brief`, {
      method: 'POST',
      body: JSON.stringify({ refresh: opts?.refresh ?? false }),
    });
  }

  // AI re-ranking of the warm board (reasoning over signals, blended client-side
  // with the formula). Returns [] when unavailable → caller falls back to formula.
  async rerankAccounts() {
    return this.request<{ ranked: { id: string; relevance: number }[]; error?: string }>(
      `/accounts/rerank`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  // Contacts
  async getContacts(params?: {
    page?: number;
    pageSize?: number;
    accountId?: string;
    state?: string;
    search?: string;
  }) {
    return this.request<{
      data: Contact[];
      total: number;
      page: number;
      pageSize: number;
      hasMore: boolean;
    }>('/contacts', { params });
  }

  async getContact(id: string) {
    return this.request<{
      contact: Contact;
      account: Account;
      engagements: EngagementEvent[];
    }>(`/contacts/${id}`);
  }

  async createContact(data: Partial<Contact>) {
    return this.request<{ id: string }>('/contacts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContact(id: string, data: Partial<Contact>) {
    return this.request<{ success: boolean }>(`/contacts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async mapContactRole(id: string, data: { role: string; influenceLevel?: string }) {
    return this.request<{ success: boolean }>(`/contacts/${id}/map-role`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async recordContactEngagement(id: string, data: { eventType: string; metadata?: Record<string, unknown> }) {
    return this.request<{ success: boolean; engagementScore: number }>(`/contacts/${id}/engage`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Opportunities
  async getOpportunities(params?: {
    page?: number;
    pageSize?: number;
    accountId?: string;
    state?: string;
    ownerId?: string;
    search?: string;
  }) {
    return this.request<{
      data: Opportunity[];
      total: number;
      page: number;
      pageSize: number;
      hasMore: boolean;
    }>('/opportunities', { params });
  }

  async getOpportunity(id: string) {
    return this.request<{
      opportunity: Opportunity;
      account: Account;
      stakeholders: OpportunityStakeholder[];
    }>(`/opportunities/${id}`);
  }

  async createOpportunity(data: Partial<Opportunity>) {
    return this.request<{ id: string }>('/opportunities', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOpportunity(id: string, data: Partial<Opportunity>) {
    return this.request<{ success: boolean }>(`/opportunities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async advanceOpportunity(id: string, data: { toStage: string; notes?: string }) {
    return this.request<{ success: boolean; newStage: string; probability: number }>(`/opportunities/${id}/advance`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async closeOpportunity(id: string, data: { won: boolean; reason?: string; lostTo?: string }) {
    return this.request<{ success: boolean }>(`/opportunities/${id}/close`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async addStakeholder(opportunityId: string, data: { contactId: string; role: string; influenceLevel?: string; sentiment?: string }) {
    return this.request<{ id: string }>(`/opportunities/${opportunityId}/stakeholders`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Pipeline/Forecast
  async getPipelineForecast() {
    return this.request<{
      byStage: Array<{ stage: string; count: number; value: number; weightedValue: number }>;
      byOwner: Array<{ ownerId: string; count: number; value: number }>;
      totalPipeline: number;
      weightedPipeline: number;
      expectedCloseThisMonth: number;
    }>('/opportunities/forecast');
  }

  // Email Integration
  async getEmailAccounts() {
    return this.request<{
      accounts: EmailAccount[];
    }>('/email/accounts');
  }

  async connectEmailAccount(provider: 'gmail' | 'outlook') {
    return this.request<{ authUrl: string }>(`/email/connect/${provider}`);
  }

  async disconnectEmailAccount(accountId: string) {
    return this.request<{ success: boolean }>(`/email/accounts/${accountId}`, {
      method: 'DELETE',
    });
  }

  async syncEmailAccount(accountId: string) {
    return this.request<{ success: boolean; message: string }>(`/email/accounts/${accountId}/sync`, {
      method: 'POST',
    });
  }

  async updateEmailAccountSettings(accountId: string, settings: Partial<EmailAccount['settings']>) {
    return this.request<{ success: boolean }>(`/email/accounts/${accountId}`, {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }

  async getEmailThreads(params?: { leadId?: string; companyId?: string; limit?: number; offset?: number }) {
    return this.request<{
      threads: EmailThread[];
    }>('/email/threads', { params });
  }

  async getEmailThread(threadId: string) {
    return this.request<{
      thread: EmailThread;
      messages: EmailMessage[];
    }>(`/email/threads/${threadId}`);
  }

  async linkEmailThread(threadId: string, data: { leadId?: string | null; companyId?: string | null }) {
    return this.request<{ success: boolean }>(`/email/threads/${threadId}/link`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async sendEmail(data: {
    accountId: string;
    to: string[];
    cc?: string[];
    subject: string;
    body: string;
    bodyType?: 'text' | 'html';
    replyToThreadId?: string;
    replyToMessageId?: string;
  }) {
    return this.request<{ success: boolean }>('/email/send', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getEmailContacts(params?: { unlinked?: boolean; limit?: number; offset?: number }) {
    return this.request<{
      contacts: EmailContact[];
    }>('/email/contacts', { params });
  }

  async linkEmailContact(contactId: string, leadId: string) {
    return this.request<{ success: boolean }>(`/email/contacts/${contactId}/link`, {
      method: 'POST',
      body: JSON.stringify({ leadId }),
    });
  }

  async createLeadFromEmailContact(contactId: string) {
    return this.request<{ success: boolean; leadId: string }>(`/email/contacts/${contactId}/create-lead`, {
      method: 'POST',
    });
  }

  // ── Meetings ──────────────────────────────────────────────────────
  async listMeetings(params?: { account?: string; page?: number; pageSize?: number }) {
    return this.request<{
      data: MeetingRow[];
      page: number;
      pageSize: number;
      hasMore: boolean;
    }>('/meetings', { params });
  }

  async getMeeting(id: string) {
    return this.request<{
      meeting: MeetingRow;
      attendees: MeetingAttendee[];
      summary: MeetingSummary | null;
    }>(`/meetings/${id}`);
  }

  async createMeeting(input: {
    title?: string;
    occurredAt?: string;
    scratchpad?: string;
    accountId?: string;
    opportunityId?: string;
    attendees?: { contactId?: string; rawName?: string; rawEmail?: string }[];
  }) {
    return this.request<{ id: string }>('/meetings', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateMeetingScratchpad(id: string, scratchpad: string) {
    return this.request<{ ok: true }>(`/meetings/${id}/scratchpad`, {
      method: 'PATCH',
      body: JSON.stringify({ scratchpad }),
    });
  }

  // Streams raw audio bytes to the worker. Whisper transcribes and the
  // updated meeting record is returned.
  async transcribeMeetingAudio(id: string, audio: Blob) {
    const token = await this.resolveToken();
    const headers: Record<string, string> = {
      'Content-Type': audio.type || 'audio/mpeg',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}/meetings/${id}/transcribe`, {
      method: 'POST',
      headers,
      body: audio,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Transcription failed' })) as { error?: string; detail?: string };
      throw new Error(err.detail || err.error || 'Transcription failed');
    }
    return response.json() as Promise<{ transcript: string; durationSec: number | null; state: 'transcribed' }>;
  }

  async summarizeMeeting(id: string) {
    return this.request<{ summary: MeetingSummary; state: 'ready' }>(`/meetings/${id}/summarize`, {
      method: 'POST',
    });
  }

  async addMeetingAttendees(id: string, attendees: { contactId?: string; rawName?: string; rawEmail?: string }[]) {
    return this.request<{ added: number; matched: number; accountId: string | null }>(
      `/meetings/${id}/attendees`,
      {
        method: 'POST',
        body: JSON.stringify({ attendees }),
      },
    );
  }

  async removeMeetingAttendee(meetingId: string, attendeeId: string) {
    return this.request<{ ok: true }>(`/meetings/${meetingId}/attendees/${attendeeId}`, {
      method: 'DELETE',
    });
  }

  async deleteMeeting(id: string) {
    return this.request<{ ok: true }>(`/meetings/${id}`, { method: 'DELETE' });
  }
}

export const api = new ApiClient();

// ── Meeting types ──────────────────────────────────────────────────
export type MeetingState =
  | 'draft'
  | 'transcribing'
  | 'transcribed'
  | 'summarizing'
  | 'ready'
  | 'failed';

export interface MeetingRow {
  id: string;
  org_id: string;
  account_id: string | null;
  account_name?: string | null;
  opportunity_id: string | null;
  owner_id: string | null;
  title: string | null;
  occurred_at: string;
  duration_sec: number | null;
  scratchpad: string | null;
  transcript: string | null;
  summary: string | null; // JSON-stringified MeetingSummary
  state: MeetingState;
  failure_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface MeetingAttendee {
  id: string;
  meeting_id: string;
  contact_id: string | null;
  contact_name?: string | null;
  contact_title?: string | null;
  contact_email?: string | null;
  raw_name: string | null;
  raw_email: string | null;
  speaker_label: string | null;
  speaking_time_sec: number | null;
  created_at: string;
}

export interface MeetingSummary {
  decisions?: string[];
  action_items?: { owner?: string; what: string; due?: string }[];
  next_steps?: string[];
  quotes?: { speaker: string; quote: string }[];
  topics?: string[];
  sentiment?: 'positive' | 'neutral' | 'negative';
  meddic_signals?: Record<string, string | undefined>;
  // Free-form summary text from records backfilled out of the legacy
  // `conversations` table (migration 0010). Rendered as a paragraph above
  // the structured fields if present.
  imported_summary?: string;
}

// Types
export interface Lead {
  id: string;
  profileUrl: string;
  name: string;
  headline?: string;
  companyName?: string;
  companyId?: string;
  location?: string;
  about?: string;
  profilePicture?: string;
  followers: number;
  connections: number;
  score: number;
  tier: 'hot' | 'warm' | 'cold';
  profileScore: number;
  engagementScore: number;
  qualityScore: number;
  status: 'new' | 'contacted' | 'in-progress' | 'qualified' | 'unqualified';
  firstSeen: string;
  lastEngagement?: string;
  lastUpdated: string;
}

export interface Company {
  id: string;
  name: string;
  domain?: string;
  score: number;
  tier: 'high-value' | 'growing' | 'low-priority';
  leadCount: number;
  avgLeadScore: number;
  momentumScore: number;
  firstSeen: string;
  lastActivity?: string;
  lastUpdated: string;
}

export interface Engagement {
  id: string;
  leadId: string;
  postId: string;
  postUrl: string;
  postText?: string;
  type: 'like' | 'comment' | 'both';
  commentText?: string;
  engagementDate?: string;
}

export interface Conversation {
  id: string;
  leadId?: string;
  companyId?: string;
  type: 'meeting' | 'call' | 'email' | 'note' | 'linkedin_message';
  title?: string;
  content?: string;
  summary?: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
  keyPoints: string[];
  nextSteps: string[];
  occurredAt?: string;
  createdAt: string;
}

export interface SOW {
  id: string;
  leadId?: string;
  companyId?: string;
  title: string;
  content: string;
  status: 'draft' | 'sent' | 'accepted' | 'rejected';
  contextSummary?: string;
  servicesIncluded: string[];
  totalEstimate?: number;
  pricingBreakdown: Array<{
    serviceId: string;
    serviceName: string;
    total: number;
  }>;
  createdAt: string;
  updatedAt: string;
  sentAt?: string;
}

export interface ServiceCatalogItem {
  id: string;
  name: string;
  description?: string;
  category?: string;
  priceType: 'fixed' | 'hourly' | 'retainer';
  basePrice?: number;
  deliverables: string[];
}

export interface Execution {
  id: string;
  executionType: 'component' | 'pipeline' | 'agent';
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  input?: unknown;
  output?: unknown;
  error?: string;
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
  tokensUsed: number;
  streamPosition: number;
  intermediateResults: unknown[];
}

export interface TelemetryEvent {
  sessionId: string;
  eventType: string;
  eventName: string;
  page?: string;
  component?: string;
  entityType?: string;
  entityId?: string;
  executionId?: string;
  properties?: Record<string, unknown>;
}

export interface TrackedPost {
  id: string;
  postUrl: string;
  authorName?: string;
  postText?: string;
  likeCount: number;
  commentCount: number;
  leadsGenerated: number;
  qualifiedLeads: number;
  dealsSourced: number;
  revenueAttributed: number;
  trackedAt: string;
  lastScraped?: string;
}

export interface SavedView {
  id: string;
  name: string;
  entityType: string;
  isDefault: boolean;
  isShared: boolean;
  columns: Array<{
    id: string;
    field: string;
    label: string;
    visible: boolean;
    width?: number;
    pinned?: 'left' | 'right';
    order: number;
  }>;
  filters: Array<{
    id: string;
    field: string;
    operator: string;
    value: unknown;
    enabled: boolean;
  }>;
  sorts: Array<{
    field: string;
    direction: 'asc' | 'desc';
    order: number;
  }>;
}

export interface EmailAccount {
  id: string;
  provider: 'gmail' | 'outlook';
  emailAddress: string;
  syncEnabled: boolean;
  lastSyncAt: string | null;
  syncError: string | null;
  syncErrorCount: number;
  settings: {
    syncSent: boolean;
    syncReceived: boolean;
    autoCreateContacts: boolean;
    autoLinkThreads: boolean;
  };
  connectedAt: string;
}

export interface EmailThread {
  id: string;
  subject: string;
  snippet: string;
  participants: Array<{ email: string; name?: string }>;
  messageCount: number;
  unreadCount: number;
  hasAttachments: boolean;
  lead: {
    id: string;
    name: string;
    profileUrl: string;
  } | null;
  company: {
    id: string;
    name: string;
  } | null;
  firstMessageAt: string;
  lastMessageAt: string;
}

export interface EmailMessage {
  id: string;
  subject: string;
  snippet: string;
  body: string;
  from: { email: string; name?: string };
  to: string[];
  cc: string[];
  direction: 'inbound' | 'outbound';
  isRead: boolean;
  hasAttachments: boolean;
  sentAt: string;
  receivedAt: string;
}

export interface EmailContact {
  id: string;
  email: string;
  name?: string;
  title?: string;
  company?: string;
  phone?: string;
  lead: {
    id: string;
    name: string;
    profileUrl: string;
  } | null;
  stats: {
    emailsSent: number;
    emailsReceived: number;
    firstEmailAt: string;
    lastEmailAt: string;
  };
  extraction: {
    confidence: number;
    source: string;
  };
}

// ===========================================
// CANONICAL ENTITY TYPES (New Data Model)
// ===========================================

export type AccountState = 'prospect' | 'qualified' | 'engaged' | 'opportunity_attached' | 'customer' | 'churned';
export type AccountTier = 'enterprise' | 'mid_market' | 'smb' | 'unqualified';

export interface Account {
  id: string;
  name: string;
  domain?: string;
  linkedinUrl?: string;
  state: AccountState;
  industry?: string;
  employeeCount?: number;
  annualRevenue?: number;
  location?: {
    city?: string;
    state?: string;
    country?: string;
  };
  icpScore: number;
  tier: AccountTier;
  qualifiedAt?: string;
  buyingSignals: BuyingSignal[];
  signalScore: number;
  lastSignalAt?: string;
  ownerId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BuyingSignal {
  type: string;
  strength: number;
  timestamp: string;
  source: string;
}

export type ContactState = 'unknown' | 'active' | 'engaged' | 'mapped' | 'inactive';
export type ContactRole = 'champion' | 'influencer' | 'blocker' | 'end_user' | 'economic_buyer' | 'technical_evaluator' | 'user_buyer' | 'coach';
export type EngagementTrend = 'heating' | 'cooling' | 'stable' | 'increasing' | 'decreasing';

export interface Contact {
  id: string;
  accountId: string;
  state: ContactState;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  linkedinUrl?: string;
  title?: string;
  department?: string;
  seniority?: string;
  profilePicture?: string;
  about?: string;
  role?: ContactRole;
  influenceScore: number;
  engagementScore: number;
  lastEngagedAt?: string;
  engagementTrend: EngagementTrend;
  followers?: number;
  connections?: number;
  ownerId?: string;
  createdAt: string;
  updatedAt: string;
}

export type OpportunityState = 'created' | 'discovery' | 'evaluation' | 'negotiation' | 'closed_won' | 'closed_lost';
export type RiskFlag = 'single_threaded' | 'champion_left' | 'stalled' | 'competitor_mentioned' | 'pushed';

export interface Opportunity {
  id: string;
  accountId: string;
  state: OpportunityState;
  name: string;
  value: number;
  currency: string;
  closeDate?: string;
  probability: number;
  meddic: MEDDICFields;
  meddicScore: number;
  healthScore: number;
  riskFlags: RiskFlag[];
  daysInStage: number;
  competitors: string[];
  competitiveNotes?: string;
  closeReason?: string;
  lostTo?: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
  stageEnteredAt: string;
}

export interface MEDDICFields {
  metrics?: string;
  economicBuyer?: string;
  decisionCriteria?: string;
  decisionProcess?: string;
  identifyPain?: string;
  champion?: string;
}

export type StakeholderRole = 'champion' | 'economic_buyer' | 'technical_evaluator' | 'coach' | 'blocker' | 'user_buyer' | 'influencer';
export type InfluenceLevel = 'decision_maker' | 'high' | 'medium' | 'low';
export type StakeholderSentiment = 'champion' | 'positive' | 'neutral' | 'negative' | 'blocker';

export interface OpportunityStakeholder {
  id: string;
  opportunityId: string;
  contactId: string;
  role: StakeholderRole;
  influenceLevel?: InfluenceLevel;
  sentiment: StakeholderSentiment;
  isPrimary?: boolean;
  notes?: string;
  lastEngagedAt?: string;
  engagementCount?: number;
  createdAt: string;
  updatedAt: string;
}

export type EngagementEventType =
  | 'email_open' | 'email_click' | 'email_reply'
  | 'meeting_scheduled' | 'meeting_attended' | 'meeting_no_show' | 'meeting'
  | 'website_visit' | 'web_visit' | 'content_download' | 'pricing_view'
  | 'webinar_registered' | 'webinar_attended'
  | 'linkedin_like' | 'linkedin_comment' | 'linkedin_view' | 'linkedin_connect' | 'linkedin_message' | 'linkedin_engage'
  | 'form_submit' | 'demo_request'
  | 'call' | 'call_completed' | 'call_voicemail'
  | 'referral_received' | 'custom';

export interface EngagementEvent {
  id: string;
  contactId?: string;
  accountId?: string;
  opportunityId?: string;
  eventType: EngagementEventType;
  weight?: number;
  occurredAt: string;
  createdAt: string;
  metadata: Record<string, unknown>;
}

export interface ActivityLog {
  id: string;
  orgId: string;
  userId: string;
  eventType: string;
  isSecurityEvent: boolean;
  resourceType: string | null;
  resourceId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  requestMethod: string | null;
  requestPath: string | null;
  details: Record<string, unknown>;
  success: boolean;
  failureReason: string | null;
  createdAt: string;
}
